import React, { useState, useCallback, useMemo } from 'react';
import Cropper from 'react-easy-crop';
import { X, Check, Crop, Eye, Save, SlidersHorizontal, Undo2, Download } from 'lucide-react';
import { getCroppedImg, buildFilter } from '../lib/cropImage';
import { ASPECTS, DEFAULT_ADJUST, OUTPUT_SIZES, CropTools, AdjustTools, ToolButton } from './PhotoEditTools';

const TABS = [
  { key: 'crop', label: 'Crop & rotate', icon: Crop },
  { key: 'adjust', label: 'Adjust', icon: SlidersHorizontal },
];

// Crop, rotate, flip, zoom and colour-adjust a photo. Default: produces an edited image (data URL).
// With `allowDisplayOnly`, the admin can instead keep the stored file and save just a
// "view" (crop/rotation/flip/filter) that PhotoFrame applies when displaying.
export default function PhotoEditModal({ src, onCancel, onConfirm, onConfirmView, allowDisplayOnly = false, initialView = null, title = 'Edit image', confirmLabel = 'Use this photo' }) {
  const iv = initialView || {};
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(iv.rot || 0);
  const [flipX, setFlipX] = useState(!!iv.flipX);
  const [flipY, setFlipY] = useState(!!iv.flipY);
  const [adjust, setAdjust] = useState({ ...DEFAULT_ADJUST, ...(iv.adjust || {}) });
  const [aspectKey, setAspectKey] = useState('free');
  const [maxSize, setMaxSize] = useState(0);
  const [tab, setTab] = useState('crop');
  const [areaPixels, setAreaPixels] = useState(null);
  const [natural, setNatural] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const aspect = ASPECTS.find((a) => a.key === aspectKey)?.value;
  const filter = useMemo(() => buildFilter(adjust), [adjust]);
  const transform = `translate(${crop.x}px, ${crop.y}px) rotate(${rotation}deg) scale(${zoom * (flipX ? -1 : 1)}, ${zoom * (flipY ? -1 : 1)})`;
  const onCropComplete = useCallback((_area, areaPx) => setAreaPixels(areaPx), []);
  const onMediaLoaded = useCallback((m) => setNatural({ nw: m.naturalWidth, nh: m.naturalHeight }), []);

  const resetAll = () => { setCrop({ x: 0, y: 0 }); setZoom(1); setRotation(0); setFlipX(false); setFlipY(false); setAdjust(DEFAULT_ADJUST); setAspectKey('free'); setMaxSize(0); };

  const render = () => getCroppedImg(src, areaPixels, rotation, { flipX, flipY, filter, maxSize });

  const confirm = async () => {
    if (!areaPixels) return;
    setBusy(true); setError('');
    try { await onConfirm(await render()); }
    catch (e) { setError('Sorry, this image could not be processed. Please try a different photo.'); }
    finally { setBusy(false); }
  };

  const download = async () => {
    if (!areaPixels) return;
    setBusy(true); setError('');
    try {
      const dataUrl = await render();
      const a = document.createElement('a');
      a.href = dataUrl; a.download = `edited-image.${dataUrl.startsWith('data:image/png') ? 'png' : 'jpg'}`; a.click();
    } catch (e) { setError('Sorry, this image could not be processed.'); }
    finally { setBusy(false); }
  };

  const confirmView = async () => {
    if (!areaPixels || !natural) return;
    setBusy(true); setError('');
    try {
      await onConfirmView({ x: areaPixels.x, y: areaPixels.y, w: areaPixels.width, h: areaPixels.height, rot: rotation, nw: natural.nw, nh: natural.nh, flipX, flipY, adjust, filter });
    } catch (e) { setError('Could not save the display settings. Please try again.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl overflow-hidden max-h-[95vh] flex flex-col" data-testid="photo-edit-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
          <h3 className="font-title text-lg flex items-center gap-2" style={{ color: 'var(--maroon)' }}><Crop size={17} /> {title}</h3>
          <div className="flex items-center gap-2">
            <ToolButton onClick={resetAll} title="Reset all edits" testId="photo-edit-reset-all"><Undo2 size={14} /> Reset</ToolButton>
            <button onClick={onCancel} disabled={busy} data-testid="photo-edit-cancel" aria-label="Close"><X size={20} /></button>
          </div>
        </div>

        <div className="relative w-full shrink-0" style={{ height: 340, background: '#222' }}>
          <Cropper image={src} crop={crop} zoom={zoom} rotation={rotation} aspect={aspect} restrictPosition={false} showGrid maxZoom={4}
            transform={transform} style={{ mediaStyle: { filter: filter || undefined } }}
            onCropChange={setCrop} onZoomChange={setZoom} onRotationChange={setRotation} onCropComplete={onCropComplete} onMediaLoaded={onMediaLoaded} />
        </div>

        <div className="px-5 pt-3 flex items-center gap-1 border-b" style={{ borderColor: 'rgba(0,0,0,0.1)' }} role="tablist">
          {TABS.map((t) => (
            <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)} data-testid={`photo-edit-tab-${t.key}`}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-title -mb-px border-b-2 transition-colors"
              style={tab === t.key ? { borderColor: 'var(--maroon)', color: 'var(--maroon)' } : { borderColor: 'transparent', color: 'var(--olive)' }}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        <div className="px-5 py-4 space-y-3 overflow-y-auto">
          {tab === 'crop' && <CropTools aspectKey={aspectKey} setAspectKey={setAspectKey} zoom={zoom} setZoom={setZoom} rotation={rotation} setRotation={setRotation} flipX={flipX} setFlipX={setFlipX} flipY={flipY} setFlipY={setFlipY} />}
          {tab === 'adjust' && <AdjustTools adjust={adjust} setAdjust={setAdjust} />}
          <div className="flex items-center gap-2 pt-1">
            <label className="text-xs font-title" style={{ color: 'var(--olive)' }} htmlFor="photo-edit-size">Saved size</label>
            <select id="photo-edit-size" value={maxSize} onChange={(e) => setMaxSize(Number(e.target.value))} data-testid="photo-edit-size"
              className="px-2 py-1 rounded border text-xs bg-white" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
              {OUTPUT_SIZES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {natural && <span className="text-xs ml-auto" style={{ color: 'var(--olive)' }} data-testid="photo-edit-dims">{areaPixels ? `${Math.round(areaPixels.width)}×${Math.round(areaPixels.height)}px` : `${natural.nw}×${natural.nh}px`}</span>}
          </div>
          {allowDisplayOnly && (
            <p className="text-xs" style={{ color: 'var(--olive)' }} data-testid="photo-edit-mode-hint">
              <strong>Display only</strong> keeps the original file and just changes how the photo is shown. <strong>Save to gallery</strong> replaces the stored image with this edited version (same link).
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t flex-wrap" style={{ borderColor: 'rgba(0,0,0,0.1)' }}>
          {error && <span className="mr-auto text-xs" data-testid="photo-edit-error" style={{ color: '#8a1f11' }}>{error}</span>}
          <button onClick={download} disabled={busy || !areaPixels} data-testid="photo-edit-download" title="Download the edited image to your computer"
            className="flex items-center gap-2 px-3 py-2 rounded text-sm font-title mr-auto" style={{ background: 'white', border: '1px solid rgba(0,0,0,0.15)', color: 'var(--navy)' }}>
            <Download size={15} /> Download
          </button>
          <button onClick={onCancel} disabled={busy} className="px-4 py-2 rounded text-sm font-title" style={{ background: 'white', border: '1px solid rgba(0,0,0,0.15)' }}>Cancel</button>
          {allowDisplayOnly && (
            <button onClick={confirmView} disabled={busy || !areaPixels || !natural} data-testid="photo-edit-display-only"
              className="flex items-center gap-2 px-4 py-2 rounded text-sm font-title border" style={{ borderColor: 'var(--navy)', color: 'var(--navy)', opacity: busy ? 0.7 : 1 }}>
              <Eye size={15} /> Display only
            </button>
          )}
          <button onClick={confirm} disabled={busy || !areaPixels} data-testid="photo-edit-confirm"
            className="flex items-center gap-2 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.7 : 1 }}>
            {allowDisplayOnly ? <Save size={15} /> : <Check size={15} />} {busy ? 'Working…' : (allowDisplayOnly ? 'Save to gallery' : confirmLabel)}
          </button>
        </div>
      </div>
    </div>
  );
}
